import jwtDecode from "jwt-decode";
import { createContext, type FC, type ReactNode, useEffect, useReducer } from "react";
import type { CountryConfiguration } from "src/api/configuration/CountryConfiguration";
import { CountryConfigurationKeyEnum } from "src/api/configuration/CountryConfigurationEnum";
import { OrganizationPreferenceKeyEnum } from "src/api/organizations/queryPreferences";
import { queryUserPreferences } from "src/api/users/query";

import SplashScreen from "src/components/SplashScreen";
import i18n from "src/config/i18n";
import { DISPLAY_DEMO } from "src/constants";
import useSessionSettings from "src/hooks/useSessionSettings";
import useSettings from "src/hooks/useSettings";
import { getLocale, setLocale } from "src/i18n";
import { UserPreferenceKeyEnum } from "src/types/enum/UserPreferenceKeyEnum";
import { OrganizationStatus } from "src/types/organization";
import type { User, UserLanguagePreferenceKeyMetadata } from "src/types/user";
import axios, { axiosMonitoring } from "src/utils/axios";

interface AuthState {
    isInitialized: boolean;
    isAuthenticated: boolean;
    user: User | null;
    userAdminId: number | null;
    countryConfigs: CountryConfiguration[] | null;
}

export interface AuthContextValue extends AuthState {
    method: "JWT";
    login: (email: string, password: string) => Promise<void>;
    logout: () => void;
    register: (email: string, name: string, password: string) => Promise<void>;
    viewAs: (userId: number, organizationStatus?: OrganizationStatus) => Promise<void>;
    viewAsAdmin: (userId: number) => Promise<void>;
    updateUser: (user: User) => void;
    fetchUser: (accessToken: string) => Promise<void>;
}

interface AuthProviderProps {
    children: ReactNode;
}

type InitialiseAction = {
    type: "INITIALISE";
    payload: {
        isAuthenticated: boolean;
        user: User | null;
        userAdminId: number | null;
        countryConfigs: CountryConfiguration[] | null;
    };
};

type LoginAction = {
    type: "LOGIN";
    payload: {
        user: User;
        userAdminId: number | null;
        countryConfigs: CountryConfiguration[] | null;
    };
};

type LogoutAction = {
    type: "LOGOUT";
};

type RegisterAction = {
    type: "REGISTER";
    payload: {
        user: User;
        userAdminId: number | null;
        countryConfigs: CountryConfiguration[] | null;
    };
};

type UpdateAction = {
    type: "UPDATE_USER";
    payload: {
        user: User;
    };
};

type Action = InitialiseAction | LoginAction | LogoutAction | RegisterAction | UpdateAction;

const initialAuthState: AuthState = {
    isAuthenticated: false,
    isInitialized: false,
    user: null,
    userAdminId: null,
    countryConfigs: [],
};

const isValidToken = (accessToken: string): boolean => {
    if (!accessToken) {
        return false;
    }

    const decoded: any = jwtDecode(accessToken);
    const currentTime = Date.now() / 1000;

    return decoded.exp > currentTime;
};

export const setSession = (accessToken: string | null): void => {
    if (accessToken) {
        localStorage.setItem("accessToken", accessToken);
        axios.defaults.headers.common.auth = `${accessToken}`;
        axiosMonitoring.defaults.headers.common.auth = `${accessToken}`;
    } else {
        localStorage.removeItem("accessToken");
        delete axios.defaults.headers.common.auth;
        delete axiosMonitoring.defaults.headers.common.auth;
    }
};

const reducer = (state: AuthState, action: Action): AuthState => {
    // Ici c'est un pseudo hack... On a pas encore l'user au moment de l'init d'i18n donc on ne peut pas lui donner en options
    // Il faudrait refaire le boot de l'appli pour être capable de charger l'user avant i18n ET react
    if (
        (action as any).payload?.countryConfigs &&
        (action as any).payload?.countryConfigs.length > 0
    ) {
        const { currencyCode, currencySymbol } = (action as any).payload.countryConfigs.find(
            (cc) => cc.configurationKey === CountryConfigurationKeyEnum.Currency
        ).metadata;
        i18n.options.interpolation.defaultVariables = {
            currencyCode,
            currencySymbol,
        };
    }

    switch (action.type) {
        case "INITIALISE": {
            const { isAuthenticated, user, userAdminId, countryConfigs } = action.payload;

            return {
                ...state,
                isAuthenticated,
                isInitialized: true,
                user,
                userAdminId,
                countryConfigs,
            };
        }
        case "LOGIN": {
            const { user, userAdminId, countryConfigs } = action.payload;

            return {
                ...state,
                isAuthenticated: true,
                user,
                userAdminId,
                countryConfigs,
            };
        }
        case "LOGOUT": {
            return {
                ...state,
                isAuthenticated: false,
                user: null,
                userAdminId: null,
                countryConfigs: [],
            };
        }
        case "REGISTER": {
            const { user, userAdminId, countryConfigs } = action.payload;

            return {
                ...state,
                isAuthenticated: true,
                user,
                userAdminId,
                countryConfigs,
            };
        }
        case "UPDATE_USER": {
            const { user } = action.payload;

            return {
                ...state,
                user: {
                    ...state.user,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    email: user.email,
                },
            };
        }
        default: {
            return { ...state };
        }
    }
};

const AuthContext = createContext<AuthContextValue>({
    ...initialAuthState,
    method: "JWT",
    login: () => Promise.resolve(),
    viewAs: () => Promise.resolve(),
    viewAsAdmin: () => Promise.resolve(),
    logout: () => {
        // RAF
    },
    register: () => Promise.resolve(),
    updateUser: () => Promise.resolve(),
    fetchUser: () => Promise.resolve(),
});

/*
 * To implement with another token in case the first token is expired;
 * a second token "refresh" token to request a new token
 */

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialAuthState);
    const { setUserInfos: setLocalSettingsUserInfos } = useSettings();
    const { setUserInfos: setSessionSettingsUserInfos, saveSettings: saveSessionSetting } =
        useSessionSettings();

    const setupSettings = (user: User | null) => {
        const userInfos = user && {
            userId: user.id,
            organizationId: user.organization.id,
        };

        setLocalSettingsUserInfos(userInfos);
        setSessionSettingsUserInfos(userInfos);
    };

    const login = async (email: string, password: string) => {
        const response = await axios.post<{
            token: string;
            user: User;
            userAdminId: number;
        }>("/auth/login", { email, password });
        const { token, user, userAdminId } = response.data;
        setSession(token);
        setupSettings(user);

        const {
            data: { configs },
        } = await axios.get<{
            configs: CountryConfiguration[];
        }>(`/configuration/countries/${user.organization.countryCode}`);

        dispatch({
            type: "LOGIN",
            payload: {
                user,
                userAdminId,
                countryConfigs: configs,
            },
        });
    };

    const viewAs = async (userId: number, organizationStatus?: OrganizationStatus) => {
        const response = await axios.post<{
            token: string;
            user: User;
            userAdminId: number;
        }>("/auth/viewas", { newUserId: userId });
        const { token, user, userAdminId } = response.data;

        const {
            data: { configs },
        } = await axios.get<{
            configs: CountryConfiguration[];
        }>(`/configuration/countries/${user.organization.countryCode}`);

        localStorage.setItem(
            DISPLAY_DEMO,
            organizationStatus === OrganizationStatus.demo ? "true" : "false"
        );

        setSession(token);
        dispatch({
            type: "LOGIN",
            payload: {
                user,
                userAdminId,
                countryConfigs: configs,
            },
        });
        setupSettings(user);
    };

    const viewAsAdmin = async (userId: number) => {
        const response = await axios.post<{
            token: string;
            user: User;
            userAdminId: number;
        }>("/auth/viewasadmin", { publicUserAdminId: userId });
        const { token, user, userAdminId } = response.data;

        const {
            data: { configs },
        } = await axios.get<{
            configs: CountryConfiguration[];
        }>(`/configuration/countries/${user.organization.countryCode}`);

        setSession(token);
        dispatch({
            type: "LOGIN",
            payload: {
                user,
                userAdminId,
                countryConfigs: configs,
            },
        });
        setupSettings(user);
    };

    const logout = () => {
        setSession(null);
        setupSettings(null);
        dispatch({ type: "LOGOUT" });
    };

    const register = async (email: string, name: string, password: string) => {
        const response = await axios.post<{
            accessToken: string;
            user: User;
            userAdminId: number;
        }>("/api/account/register", {
            email,
            name,
            password,
        });
        const { accessToken, user, userAdminId } = response.data;

        const {
            data: { configs },
        } = await axios.get<{
            configs: CountryConfiguration[];
        }>(`/configuration/countries/${user.organization.countryCode}`);

        window.localStorage.setItem("accessToken", accessToken);

        dispatch({
            type: "REGISTER",
            payload: {
                user,
                userAdminId,
                countryConfigs: configs,
            },
        });
    };

    const updateUser = (user: User) => {
        dispatch({
            type: "UPDATE_USER",
            payload: {
                user,
            },
        });
    };

    const fetchUser = async (accessToken: string): Promise<void> => {
        if (accessToken && isValidToken(accessToken)) {
            setSession(accessToken);
            try {
                const response = await axios.get<{
                    token: string;
                    user: User;
                    userAdminId: number;
                }>("/auth/me");
                if (response.status !== 200) {
                    dispatch({
                        type: "INITIALISE",
                        payload: {
                            isAuthenticated: false,
                            user: null,
                            userAdminId: null,
                            countryConfigs: [],
                        },
                    });
                    return;
                }

                const { token, user, userAdminId } = response.data;
                setSession(token);
                setupSettings(user);

                const {
                    data: { configs },
                } = await axios.get<{
                    configs: CountryConfiguration[];
                }>(`/configuration/countries/${user.organization.countryCode}`);

                if (user.organization.organizationPreferences) {
                    const priceDisplayPreference = user.organization.organizationPreferences.find(
                        (orgPref) =>
                            orgPref.key === OrganizationPreferenceKeyEnum.PRICE_DISPLAY_MODE
                    );
                    if (priceDisplayPreference) {
                        saveSessionSetting({
                            pricesDisplay: priceDisplayPreference.value.vat ?? "inclvat",
                        });
                    }
                }

                dispatch({
                    type: "INITIALISE",
                    payload: {
                        isAuthenticated: true,
                        user,
                        userAdminId,
                        countryConfigs: configs,
                    },
                });
            } catch {
                dispatch({
                    type: "INITIALISE",
                    payload: {
                        isAuthenticated: false,
                        user: null,
                        userAdminId: null,
                        countryConfigs: [],
                    },
                });
            }
        } else {
            dispatch({
                type: "INITIALISE",
                payload: {
                    isAuthenticated: false,
                    user: null,
                    userAdminId: null,
                    countryConfigs: [],
                },
            });
        }
    };

    useEffect(() => {
        try {
            const accessToken = window.localStorage.getItem("accessToken");

            fetchUser(accessToken);
        } catch (err) {
            console.error(err);
            dispatch({
                type: "INITIALISE",
                payload: {
                    isAuthenticated: false,
                    user: null,
                    userAdminId: null,
                    countryConfigs: [],
                },
            });
        }
    }, []);

    useEffect(() => {
        const userId = state.user?.id;
        if (!state.isInitialized || !userId) {
            return;
        }

        queryUserPreferences(userId).then((response) => {
            const locale = getLocale();

            const { preferences } = response.data;
            const languageMetadata = preferences.find(
                (elt) => elt.preferenceKey === UserPreferenceKeyEnum.Language
            ).metaData as UserLanguagePreferenceKeyMetadata;

            setLocale(languageMetadata.language);
            if (locale !== languageMetadata.language) {
                location.reload();
            }
        });
    }, [state.isInitialized, state.user?.id]);

    if (!state.isInitialized && window.location.pathname !== "/error") {
        return <SplashScreen />;
    }

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: "JWT",
                login,
                logout,
                register,
                viewAs,
                viewAsAdmin,
                updateUser,
                fetchUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
